import type { SegmentType, TemporalKind } from './segments';
import { pad, type CalendarDate } from './temporal';

/**
 * How the date segments are ordered: `day-first` (the default) turns a month-first locale such as
 * en-US into dd/mm/yyyy and leaves day-first and year-first locales as they are; `locale` keeps
 * the locale's own order.
 */
export type DateOrder = 'day-first' | 'locale';

/** One piece of the field: a segment or the literal text between two. */
export type LayoutItem = { kind: 'segment'; type: SegmentType } | { kind: 'literal'; text: string };

export interface TemporalLocale {
  locale: string;
  hour12: boolean;
  /** First day of the week, Sunday = 0. */
  weekStart: number;
  layouts: Record<TemporalKind, LayoutItem[]>;
  /** January first. */
  monthNames: string[];
  monthShortNames: string[];
  /** Sunday first; two letters where the script allows ("Su"), as Chrome shows them. */
  weekdayShortNames: string[];
  weekdayLongNames: string[];
  /** [AM, PM]. */
  periodNames: [string, string];
  /** "October 2026". */
  formatMonthYear(year: number, month: number): string;
  /** "Friday, October 23, 2026". */
  formatFullDate(date: CalendarDate): string;
}

/** Regions whose week starts on Sunday or Saturday, for engines without `getWeekInfo`. */
const SUNDAY_REGIONS = new Set(
  'AG AS BD BR BS BT BW BZ CA CN CO DM DO ET GT GU HK HN ID IL IN JM JP KE KH KR LA MH MM MO MT MX MZ NI NP PA PE PH PK PR PT PY SA SG SV TH TT TW UM US VE VI WS YE ZA ZW'.split(
    ' ',
  ),
);
const SATURDAY_REGIONS = new Set('AE AF BH DJ DZ EG IQ IR JO KW LY OM QA SD SY'.split(' '));

interface WeekInfoLocale extends Intl.Locale {
  getWeekInfo?: () => { firstDay: number };
  weekInfo?: { firstDay: number };
}

/** Sunday = 0; from `Intl.Locale#getWeekInfo` (ISO 1–7) or the region when that is missing. */
export function weekStartFor(locale: string): number {
  try {
    const intl = new Intl.Locale(locale) as WeekInfoLocale;
    const info = typeof intl.getWeekInfo === 'function' ? intl.getWeekInfo() : intl.weekInfo;
    if (info && Number.isInteger(info.firstDay)) return info.firstDay % 7;
    const region = intl.maximize().region ?? '';
    if (SUNDAY_REGIONS.has(region)) return 0;
    if (SATURDAY_REGIONS.has(region)) return 6;
  } catch {
    // A malformed locale tag: Monday, the ISO default.
  }
  return 1;
}

function safeLocale(locale: string): string {
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([locale])[0] ?? 'en-US';
  } catch {
    return 'en-US';
  }
}

/** A UTC instant for wall-clock parts, so formatting with `timeZone: 'UTC'` reads them back. */
function utcInstant(year: number, month: number, day: number, hour = 0, minute = 0): Date {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, 0, 0);
  return date;
}

const PART_TO_SEGMENT: Partial<Record<Intl.DateTimeFormatPartTypes, SegmentType>> = {
  year: 'year',
  month: 'month',
  day: 'day',
  hour: 'hour',
  minute: 'minute',
  dayPeriod: 'period',
};

/** Bidi marks dropped; ICU's narrow no-break space before AM/PM read as a plain space. */
function cleanLiteral(text: string): string {
  return text.replace(/[\u200e\u200f\u061c]/g, '').replace(/[\u202f\u00a0]/g, ' ');
}

function layoutFrom(parts: Intl.DateTimeFormatPart[]): LayoutItem[] {
  const items: LayoutItem[] = [];
  for (const part of parts) {
    const type = PART_TO_SEGMENT[part.type];
    if (type) {
      items.push({ kind: 'segment', type });
    } else if (part.type === 'literal') {
      const text = cleanLiteral(part.value);
      const last = items.at(-1);
      if (last?.kind === 'literal') last.text += text;
      else if (text) items.push({ kind: 'literal', text });
    }
  }
  while (items[0]?.kind === 'literal') items.shift();
  while (items.at(-1)?.kind === 'literal') items.pop();
  return items;
}

function twoLetterWeekday(name: string): string {
  // Latin, Greek and Cyrillic abbreviations read well at two letters ("Su", "Mo"); others stay.
  return /^[\p{Script=Latin}\p{Script=Greek}\p{Script=Cyrillic}]{3,}\.?$/u.test(name)
    ? name.slice(0, 2)
    : name;
}

/** Month, day, year becomes day, month, year; the literals between them stay where they are. */
function dayFirst(layout: LayoutItem[]): LayoutItem[] {
  const at = (type: SegmentType) =>
    layout.findIndex((item) => item.kind === 'segment' && item.type === type);
  const month = at('month');
  const day = at('day');
  const year = at('year');
  if (month < 0 || day < 0 || year < 0 || !(month < day && day < year)) return layout;
  return layout.map((item, index) => {
    if (index === month) return { kind: 'segment', type: 'day' };
    if (index === day) return { kind: 'segment', type: 'month' };
    return item;
  });
}

const cache = new Map<string, TemporalLocale>();

/** Everything locale-dependent about the pickers, from `Intl`; cached per locale tag and order. */
export function getTemporalLocale(
  requested: string,
  dateOrder: DateOrder = 'day-first',
): TemporalLocale {
  const key = `${requested}|${dateOrder}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const order = (layout: LayoutItem[]) => (dateOrder === 'day-first' ? dayFirst(layout) : layout);
  const locale = safeLocale(requested);
  const hourCycle = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions()
    .hourCycle;
  const hour12 = hourCycle === 'h12' || hourCycle === 'h11';
  const utc = { timeZone: 'UTC' } as const;
  const sample = utcInstant(2026, 11, 23, 21, 45);
  const dateParts = { year: 'numeric', month: '2-digit', day: '2-digit' } as const;
  const timeParts = { hour: '2-digit', minute: '2-digit', hour12 } as const;
  const layouts: Record<TemporalKind, LayoutItem[]> = {
    date: order(
      layoutFrom(new Intl.DateTimeFormat(locale, { ...utc, ...dateParts }).formatToParts(sample)),
    ),
    time: layoutFrom(
      new Intl.DateTimeFormat(locale, { ...utc, ...timeParts }).formatToParts(sample),
    ),
    datetime: order(
      layoutFrom(
        new Intl.DateTimeFormat(locale, { ...utc, ...dateParts, ...timeParts }).formatToParts(
          sample,
        ),
      ),
    ),
  };
  const monthLong = new Intl.DateTimeFormat(locale, { ...utc, month: 'long' });
  const monthShort = new Intl.DateTimeFormat(locale, { ...utc, month: 'short' });
  const months = Array.from({ length: 12 }, (_, i) => utcInstant(2026, i + 1, 15));
  // 2026-11-22 is a Sunday.
  const weekdays = Array.from({ length: 7 }, (_, i) => utcInstant(2026, 11, 22 + i));
  const weekdayShort = new Intl.DateTimeFormat(locale, { ...utc, weekday: 'short' });
  const weekdayLong = new Intl.DateTimeFormat(locale, { ...utc, weekday: 'long' });
  const periodFormat = new Intl.DateTimeFormat(locale, { ...utc, hour: 'numeric', hour12: true });
  const periodOf = (hour: number, fallback: string) =>
    periodFormat.formatToParts(utcInstant(2026, 1, 1, hour)).find((p) => p.type === 'dayPeriod')
      ?.value ?? fallback;
  const monthYear = new Intl.DateTimeFormat(locale, { ...utc, year: 'numeric', month: 'long' });
  const fullDate = new Intl.DateTimeFormat(locale, {
    ...utc,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const result: TemporalLocale = {
    locale,
    hour12,
    weekStart: weekStartFor(locale),
    layouts,
    monthNames: months.map((d) => monthLong.format(d)),
    monthShortNames: months.map((d) => monthShort.format(d)),
    weekdayShortNames: weekdays.map((d) => twoLetterWeekday(weekdayShort.format(d))),
    weekdayLongNames: weekdays.map((d) => weekdayLong.format(d)),
    periodNames: [periodOf(9, 'AM'), periodOf(21, 'PM')],
    formatMonthYear: (year, month) => monthYear.format(utcInstant(year, month, 1)),
    formatFullDate: (date) => fullDate.format(utcInstant(date.year, date.month, date.day)),
  };
  cache.set(key, result);
  return result;
}

/** The text a segment shows for a value: two digits, four for the year, the period's name. */
export function segmentDisplay(type: SegmentType, value: number, locale: TemporalLocale): string {
  if (type === 'period') return locale.periodNames[value === 1 ? 1 : 0];
  return pad(value, type === 'year' ? 4 : 2);
}
